import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Form,
  FormActions,
  FormSection,
  GridFrame,
  GridScroll,
  Icon,
  LoadingState,
  PageHeader,
  SelectField,
  StatusChip,
  Table,
  Td,
  TextField,
  Th,
  Tr,
  statusTone,
} from '@tessma/design-system';
import { Refusal, refusalText, formatDay, formatMoney, useAsync } from '../ui/async.js';
import type { PartiesGateway } from '../parties/gateway.js';
import { AddPartySheet } from './inline-create.js';
import type {
  Account,
  Bill,
  DuplicateCandidate,
  FinanceDimension,
  FinanceGateway,
  FinancePaymentTerm,
  FinanceSupplier,
  InvoiceLine,
  SettlementDocument,
  SupplierCredit,
} from './gateway.js';

/**
 * ACCOUNTS PAYABLE — the mirror of receivables, with the three differences the
 * domain has, shown rather than hidden.
 *
 * **DUPLICATES ARE REPORTED, NOT BLOCKED.** When the server returns
 * `possibleDuplicates`, this screen shows them and the bill is still created.
 * Two genuinely different bills from one supplier on one day for one amount is
 * an ordinary Tuesday; the only case the platform refuses outright is an exact
 * repeat of the supplier's own reference, and the DATABASE refuses that, as a
 * 409 this screen relays.
 *
 * **APPROVING AND POSTING ARE TWO BUTTONS BECAUSE THEY ARE TWO AUTHORITIES.**
 * An approval carries none of its own: the server re-checks the permission,
 * the reach and the period when Post is pressed.
 */

// ---- 1. Supplier bills ------------------------------------------------------

export function BillsScreen({
  gateway,
  legalEntityId,
  canEnter,
  onOpen,
  onNew,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
  readonly canEnter: boolean;
  readonly onOpen: (billId: string) => void;
  readonly onNew: () => void;
}) {
  const { state, reload } = useAsync<readonly Bill[]>(
    () => gateway.bills(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading bills…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  // The page's own header: see the note on the sales invoice list. The same
  // three words appeared in the shell's `<h1>` and again immediately below it.
  const header = (
    <PageHeader
      description="What you owe, within your reach."
      actions={
        canEnter ? (
          <Button variant="primary" onClick={onNew}>
            Enter a bill
          </Button>
        ) : undefined
      }
    />
  );

  if (state.value.length === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon="bank"
            title="No bills"
            description="Bills within your reach appear here."
          />
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      <Card padding="flush">
        <GridFrame>
          <GridScroll>
            <Table label="Supplier bills">
              <thead>
                <Tr>
                  <Th>Their reference</Th>
                  <Th>Status</Th>
                  <Th>Bill date</Th>
                  <Th>Due</Th>
                  <Th align="right">Net</Th>
                  <Th align="right">Tax</Th>
                  <Th align="right">Gross</Th>
                </Tr>
              </thead>
              <tbody>
                {state.value.map((bill) => (
                  <Tr key={bill.id}>
                    <Td>
                      <Button
                        variant="tertiary"
                        onClick={() => {
                          onOpen(bill.id);
                        }}
                      >
                        {bill.supplierReference}
                      </Button>
                    </Td>
                    <Td>
                      <StatusChip tone={statusTone(bill.status)}>{bill.status}</StatusChip>
                    </Td>
                    <Td variant="secondary">{formatDay(bill.billDate)}</Td>
                    <Td variant="secondary">{formatDay(bill.dueDate)}</Td>
                    <Td variant="money">{formatMoney(bill.netTotal, bill.currency)}</Td>
                    <Td variant="money">{formatMoney(bill.taxTotal, bill.currency)}</Td>
                    <Td variant="money">{formatMoney(bill.grossTotal, bill.currency)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>
    </>
  );
}

// ---- 2. Bill detail: approve, then post ------------------------------------

export function BillDetailScreen({
  gateway,
  billId,
  canApprove,
  canPost,
  canSubmit = false,
}: {
  readonly gateway: FinanceGateway;
  readonly billId: string;
  readonly canApprove: boolean;
  readonly canPost: boolean;
  /** `finance.bill.create` — the enterer asks for approval; the engine routes it. */
  readonly canSubmit?: boolean;
}) {
  const { state, reload } = useAsync<{ bill: Bill; lines: readonly InvoiceLine[] }>(
    () => gateway.bill(billId),
    [gateway, billId],
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [posted, setPosted] = useState<string | undefined>(undefined);
  const [submitted, setSubmitted] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading bill…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { bill, lines } = state.value;

  async function act(what: 'approve' | 'post' | 'submit') {
    setBusy(true);
    setProblem(undefined);
    try {
      if (what === 'submit') {
        const result = await gateway.submitBill(billId);
        // THE SERVER SAYS WHETHER A ROUTE EXISTED. A screen that assumed one
        // would show "sent for approval" in a tenant where nothing was sent.
        setPosted(undefined);
        setSubmitted(
          result.routed
            ? 'Sent for approval. The tenant’s route decides who is asked.'
            : 'No approval route is published for bills, so this is still a draft and can be approved directly.',
        );
      } else if (what === 'approve') {
        await gateway.approveBill(billId);
      } else {
        const result = await gateway.postBill(billId);
        setPosted(result.journalId);
      }
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The server refused that.'));
    } finally {
      setBusy(false);
    }
  }

  const actions = (
    <>
      {canSubmit && bill.status === 'draft' ? (
        <Button
          variant="tertiary"
          disabled={busy}
          onClick={() => {
            void act('submit');
          }}
        >
          Send for approval
        </Button>
      ) : null}
      {canApprove && (bill.status === 'draft' || bill.status === 'pending_approval') ? (
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => {
            void act('approve');
          }}
        >
          Approve
        </Button>
      ) : null}
      {canPost && bill.status === 'approved' ? (
        <Button
          variant="primary"
          disabled={busy}
          onClick={() => {
            void act('post');
          }}
        >
          Post to the ledger
        </Button>
      ) : null}
    </>
  );

  return (
    <div>
      <Card>
        <CardHeader
          title={bill.supplierReference}
          subtitle={`${bill.status} · ${formatMoney(bill.grossTotal, bill.currency)}`}
          action={actions}
        />
        {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
        {submitted !== undefined ? <Alert tone="info" title={submitted} /> : null}
        {BigInt(bill.reverseChargeTax) !== 0n ? (
          /*
           * FR-VAT-008. The supplier charged nothing and WE account for the
           * VAT: it is declared in box 1 and reclaimed in box 4, and it is not
           * part of what we pay them. Shown here because a bill whose total is
           * the net and whose VAT return is not looks like an error until
           * somebody says why it is not.
           */
          <Alert tone="info" title="Domestic reverse charge">
            {formatMoney(bill.reverseChargeTax, bill.currency)} of VAT is ours to declare and
            reclaim on this purchase. The supplier is paid{' '}
            {formatMoney(bill.grossTotal, bill.currency)}.
          </Alert>
        ) : null}
        {posted !== undefined ? (
          <Alert tone="success" title="Posted">
            Journal {posted}.
          </Alert>
        ) : null}
        {bill.status === 'approved' ? (
          <Alert tone="info" title="An approval grants no authority of its own">
            Posting re-checks the permission, the reach and the accounting period at the moment it
            happens. An approved bill whose period has since closed is refused.
          </Alert>
        ) : null}
      </Card>

      <Card padding="flush">
        <CardHeader title="Lines" />
        <GridFrame>
          <GridScroll>
            <Table label="Bill lines">
              <thead>
                <Tr>
                  <Th>#</Th>
                  <Th>Description</Th>
                  <Th align="right">Net</Th>
                  <Th>Tax code</Th>
                  <Th align="right">Tax</Th>
                </Tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <Tr key={line.id}>
                    <Td variant="secondary">{line.lineNo}</Td>
                    <Td>{line.description}</Td>
                    <Td variant="money">{formatMoney(line.netAmount, bill.currency)}</Td>
                    <Td variant="secondary">{line.taxCode ?? '—'}</Td>
                    <Td variant="money">{formatMoney(line.taxAmount, bill.currency)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>
    </div>
  );
}

// ---- 3. Entering a bill, with duplicate advice ------------------------------

export function BillEntryScreen({
  gateway,
  parties,
  legalEntityId,
  canAddSupplier,
  onEntered,
}: {
  readonly gateway: FinanceGateway;
  readonly parties: PartiesGateway;
  readonly legalEntityId: string;
  /** A courtesy. The server refuses the two writes again if it is wrong. */
  readonly canAddSupplier: boolean;
  readonly onEntered: (bill: Bill) => void;
}) {
  const { state, reload } = useAsync<{
    suppliers: readonly FinanceSupplier[];
    accounts: readonly Account[];
    dimensions: readonly FinanceDimension[];
  }>(
    async () => ({
      suppliers: await gateway.suppliers(legalEntityId),
      accounts: await gateway.accounts(legalEntityId),
      dimensions: await gateway.dimensions(legalEntityId),
    }),
    [gateway, legalEntityId],
  );
  const [partyId, setPartyId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [billDate, setBillDate] = useState('');
  const [description, setDescription] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [costCentreId, setCostCentreId] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [duplicates, setDuplicates] = useState<readonly DuplicateCandidate[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  // The supplier list after one was added — re-read from the server, never
  // appended to here. See the note on the invoice editor: `reload()` would
  // discard the bill being entered, which is the interruption this removes.
  const [reread, setReread] = useState<readonly FinanceSupplier[] | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { accounts } = state.value;
  const suppliers = reread ?? state.value.suppliers;
  const expenses = accounts.filter((account) => account.accountType === 'expense');
  /**
   * COST CENTRES ONLY, AND THE KIND IS WHY.
   *
   * One table holds cost centres, departments, projects and jobs, and the
   * posting engine refuses a line whose dimension is of the wrong kind. This
   * offers the ones the field is for — which is the picker doing its job, not
   * the browser hiding records: nothing here is withheld on grounds of who is
   * asking, and the server checks the kind again when the bill posts.
   *
   * An inactive one is left out for the same reason it is inactive.
   */
  const costCentres = state.value.dimensions.filter(
    (dimension) => dimension.dimensionKind === 'cost_centre' && dimension.isActive,
  );

  async function enter() {
    setBusy(true);
    setProblem(undefined);
    setDuplicates([]);
    try {
      const result = await gateway.enterBill({
        legalEntityId,
        partyId,
        supplierReference: reference,
        billDate,
        currency: 'GBP',
        lines: [
          {
            description,
            quantity: '1',
            unitPrice,
            taxCode: 'GB_STD',
            accountId,
            // Omitted entirely when nobody chose one: an empty string is not a
            // dimension, and the server would rightly refuse it as an id.
            ...(costCentreId === '' ? {} : { costCentreId }),
          },
        ],
      });
      setDuplicates(result.possibleDuplicates);
      onEntered(result.bill);
    } catch (error) {
      // A 409 here is the DATABASE refusing an exact repeat of the supplier's
      // reference. The message says which bill it clashes with.
      setProblem(refusalText(error, 'The bill could not be entered.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Card>
        <CardHeader
          title="Enter a supplier bill"
          subtitle="Their reference, their date, their tax."
        />
        {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void enter();
          }}
        >
          <FormSection title="The bill">
            <SelectField
              label="Supplier"
              value={partyId}
              onChange={(event) => {
                setPartyId(event.target.value);
              }}
              options={suppliers.map((supplier) => ({
                value: supplier.partyId,
                label: supplier.displayName,
              }))}
              placeholder="Choose a supplier"
              required
            />
            {canAddSupplier && (
              <Button
                variant="secondary"
                icon={<Icon name="plus" size={14} />}
                onClick={() => {
                  setAddOpen(true);
                }}
              >
                Add a supplier
              </Button>
            )}
            <SelectField
              label="Expense account"
              value={accountId}
              onChange={(event) => {
                setAccountId(event.target.value);
              }}
              options={expenses.map((account) => ({
                value: account.id,
                label: `${account.code} ${account.name}`,
              }))}
              placeholder="Choose an account"
              required
            />
            <TextField
              label="Supplier reference"
              value={reference}
              onChange={(event) => {
                setReference(event.target.value);
              }}
              helper="Their invoice number. The same one twice is refused."
              required
            />
            <TextField
              label="Bill date"
              type="date"
              value={billDate}
              onChange={(event) => {
                setBillDate(event.target.value);
              }}
              helper="Their date, which is the tax point."
              required
            />
            <TextField
              label="Description"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
              required
            />
            {costCentres.length > 0 && (
              <SelectField
                label="Cost centre"
                value={costCentreId}
                onChange={(event) => {
                  setCostCentreId(event.target.value);
                }}
                options={costCentres.map((dimension) => ({
                  value: dimension.id,
                  label: `${dimension.code} — ${dimension.name}`,
                }))}
                placeholder="Not coded to a cost centre"
                helper="Where this cost belongs. One bill can cover two of them, so it is set per line."
              />
            )}
            <TextField
              label="Net amount, in minor units"
              value={unitPrice}
              onChange={(event) => {
                setUnitPrice(event.target.value);
              }}
              helper="Pence. The tax is computed by the server at their date."
              required
            />
          </FormSection>
          <FormActions submitLabel="Enter bill" submitting={busy} />
        </Form>
      </Card>

      {duplicates.length > 0 ? (
        <Card>
          <CardHeader
            title="Possible duplicates"
            subtitle="Reported for a person to judge. The bill was still entered."
          />
          <GridFrame>
            <GridScroll>
              <Table label="Possible duplicates">
                <thead>
                  <Tr>
                    <Th>Their reference</Th>
                    <Th>Date</Th>
                    <Th align="right">Gross</Th>
                    <Th>Why</Th>
                  </Tr>
                </thead>
                <tbody>
                  {duplicates.map((candidate) => (
                    <Tr key={candidate.billId}>
                      <Td>{candidate.supplierReference}</Td>
                      <Td variant="secondary">{formatDay(candidate.billDate)}</Td>
                      <Td variant="money">{formatMoney(candidate.grossTotal, 'GBP')}</Td>
                      <Td variant="secondary">{candidate.basis.replace(/_/g, ' ')}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        </Card>
      ) : null}

      <AddPartySheet
        role="supplier"
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        finance={gateway}
        parties={parties}
        legalEntityId={legalEntityId}
        onCreated={(created) => {
          setPartyId(created.partyId);
          void gateway.suppliers(legalEntityId).then(setReread, () => {
            // A failed re-read is not a failed create; see the invoice editor.
          });
        }}
      />
    </div>
  );
}

// ---- 4. Supplier credits ----------------------------------------------------

export function SupplierCreditsScreen({
  gateway,
  legalEntityId,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
}) {
  const { state, reload } = useAsync<readonly SupplierCredit[]>(
    () => gateway.supplierCredits(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading supplier credits…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  if (state.value.length === 0) {
    return (
      <Card>
        <CardHeader title="Supplier credits" />
        <EmptyState
          icon="bank"
          title="No supplier credits"
          description="A credit your supplier raised appears here once it is recorded."
        />
      </Card>
    );
  }

  return (
    <Card padding="flush">
      <CardHeader
        title="Supplier credits"
        subtitle="Recorded, not issued: they already happened."
      />
      <GridFrame>
        <GridScroll>
          <Table label="Supplier credits">
            <thead>
              <Tr>
                <Th>Their reference</Th>
                <Th>Status</Th>
                <Th>Date</Th>
                <Th align="right">Gross</Th>
              </Tr>
            </thead>
            <tbody>
              {state.value.map((credit) => (
                <Tr key={credit.id}>
                  <Td>{credit.supplierReference}</Td>
                  <Td>
                    <StatusChip tone={statusTone(credit.status)}>{credit.status}</StatusChip>
                  </Td>
                  <Td variant="secondary">{formatDay(credit.creditDate)}</Td>
                  <Td variant="money">{formatMoney(credit.grossTotal, credit.currency)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </GridScroll>
      </GridFrame>
    </Card>
  );
}

// ---- 5. Payments and allocation --------------------------------------------

export function PaymentsScreen({
  gateway,
  legalEntityId,
  canAllocate,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
  readonly canAllocate: boolean;
}) {
  const { state, reload } = useAsync<{
    payments: readonly SettlementDocument[];
    bills: readonly Bill[];
  }>(
    async () => ({
      payments: await gateway.payments(legalEntityId),
      bills: await gateway.bills(legalEntityId),
    }),
    [gateway, legalEntityId],
  );

  const [paymentId, setPaymentId] = useState('');
  const [billId, setBillId] = useState('');
  const [amount, setAmount] = useState('');
  const [outcome, setOutcome] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading payments…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { payments, bills } = state.value;

  async function allocate() {
    setProblem(undefined);
    setOutcome(undefined);
    try {
      const result = await gateway.allocatePayable({
        sourceKind: 'payment',
        sourceId: paymentId,
        targetId: billId,
        amount,
      });
      setOutcome(
        `Applied. The bill is ${result.targetStatus} with ${result.targetOutstanding} ` +
          `outstanding; ${result.sourceRemaining} remains on the payment.`,
      );
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The allocation was refused.'));
    }
  }

  return (
    <div>
      <Card padding="flush">
        <CardHeader title="Payments" subtitle="Money paid, and what is left to apply." />
        {payments.length === 0 ? (
          <EmptyState icon="bank" title="No payments" description="Money paid appears here." />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label="Payments">
                <thead>
                  <Tr>
                    <Th>Number</Th>
                    <Th>Paid</Th>
                    <Th align="right">Amount</Th>
                    <Th align="right">Applied</Th>
                    <Th align="right">Remaining</Th>
                  </Tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <Tr key={payment.id}>
                      <Td>{payment.number ?? '—'}</Td>
                      <Td variant="secondary">{formatDay(payment.occurredOn)}</Td>
                      <Td variant="money">{formatMoney(payment.amount, payment.currency)}</Td>
                      <Td variant="money">{formatMoney(payment.allocated, payment.currency)}</Td>
                      <Td variant="money">{formatMoney(payment.remaining, payment.currency)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>

      {canAllocate ? (
        <Card>
          <CardHeader
            title="Apply a payment"
            subtitle="Partial payment is ordinary. Paying more than is owed is refused."
          />
          {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
          {outcome !== undefined ? <Alert tone="success" title={outcome} /> : null}
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void allocate();
            }}
          >
            <FormSection title="Allocation">
              <TextField
                label="Payment"
                value={paymentId}
                onChange={(event) => {
                  setPaymentId(event.target.value);
                }}
                helper={`${String(payments.length)} payment(s) available.`}
                required
              />
              <TextField
                label="Bill"
                value={billId}
                onChange={(event) => {
                  setBillId(event.target.value);
                }}
                helper={`${String(bills.length)} bill(s) within your reach.`}
                required
              />
              <TextField
                label="Amount, in minor units"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                }}
                required
              />
            </FormSection>
            <FormActions submitLabel="Apply" />
          </Form>
        </Card>
      ) : null}
    </div>
  );
}

// ---- 6. Suppliers -----------------------------------------------------------

/**
 * **SUPPLIERS — Finance's view of a party it buys from.**
 *
 * The same shape as Customers, and the same rule: the organisation lives in
 * Party Master and `finance_supplier` holds only the terms, the coding and the
 * bank detail Finance owns. One organisation a tenant both buys from and sells
 * to is ONE party with two relationships, which is why this screen and the
 * Customers screen can show the same name without either owning it.
 *
 * **THE BANK ACCOUNT IS FOUR DIGITS AND HAS NEVER BEEN MORE.** The server
 * stores and returns `bank_account_last4`; the full number is not in the
 * payload, so it cannot be in this table however the column were designed.
 * That is the point of the column being last4 in the first place — supplier
 * bank details are the target of invoice-redirection fraud, and changing them
 * is a separately permissioned act (`finance.supplier.bank_details`).
 */
export function SuppliersScreen({
  gateway,
  legalEntityId,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
}) {
  const { state, reload } = useAsync<{
    suppliers: readonly FinanceSupplier[];
    terms: readonly FinancePaymentTerm[];
  }>(
    async () => ({
      suppliers: await gateway.suppliers(legalEntityId),
      terms: legalEntityId === undefined ? [] : await gateway.paymentTerms(legalEntityId),
    }),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading suppliers…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { suppliers, terms } = state.value;
  const termName = (id: string | null) =>
    id === null ? '—' : (terms.find((term) => term.id === id)?.name ?? '—');

  const header = <PageHeader description="Who you buy from, and the terms you buy on." />;

  if (suppliers.length === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon="users-three"
            title="No suppliers"
            description="Suppliers within your reach appear here. A supplier is an organisation in your shared records with a Finance relationship for this entity."
          />
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      <Card padding="flush">
        <GridFrame>
          <GridScroll>
            <Table label="Suppliers">
              <thead>
                <Tr>
                  <Th>Supplier</Th>
                  <Th>Standing</Th>
                  <Th>Payment terms</Th>
                  <Th>Default tax code</Th>
                  <Th>Bank account</Th>
                </Tr>
              </thead>
              <tbody>
                {suppliers.map((supplier) => (
                  <Tr key={`${supplier.legalEntityId}:${supplier.partyId}`}>
                    <Td>{supplier.displayName}</Td>
                    <Td>
                      {supplier.isActive ? (
                        <StatusChip tone="success">Active</StatusChip>
                      ) : (
                        <StatusChip tone="neutral">Inactive</StatusChip>
                      )}
                    </Td>
                    <Td variant="secondary">{termName(supplier.paymentTermId)}</Td>
                    <Td variant="secondary">{supplier.defaultTaxCode ?? '—'}</Td>
                    <Td variant="secondary">
                      {/*
                        NOT HELD IS NOT A NUMBER. A dash says the server holds
                        no bank detail for this supplier; anything else here
                        would be four digits it does hold.
                      */}
                      {supplier.bankAccountLast4 === null
                        ? '—'
                        : `Ending ${supplier.bankAccountLast4}`}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>
    </>
  );
}
